import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  CheckCircle2,
  CircleAlert,
  FileCheck2,
  Lightbulb,
  LockKeyhole,
  Mail,
  MapPin,
  Phone,
  RotateCcw,
  Scale,
  UserRound,
} from "lucide-react";
import {
  INNOVATOR_FOUNDER,
  PROGRAMMES,
  assessInnovatorFounder,
} from "@/programmes/innovatorFounder";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const answerOptions = (allowNotApplicable) => [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "unsure", label: "Not sure" },
  ...(allowNotApplicable ? [{ value: "na", label: "Not applicable" }] : []),
];

const toneClasses = {
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-950",
  amber: "border-amber-200 bg-amber-50 text-amber-950",
  rose: "border-rose-200 bg-rose-50 text-rose-950",
};

const emptyCandidate = {
  fullName: "",
  email: "",
  phone: "",
  country: "",
  city: "",
  consent: false,
  whatsappMarketing: false,
};

const emailLooksValid = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
const phoneLooksValid = (value) => /^\+[1-9]\d{6,14}$/.test(value.replace(/[\s().-]/g, ""));

function validateCandidate(candidate) {
  const errors = {};
  if (candidate.fullName.trim().length < 2) errors.fullName = "Enter the candidate's full name.";
  if (!emailLooksValid(candidate.email)) errors.email = "Enter a valid email address.";
  if (!phoneLooksValid(candidate.phone)) errors.phone = "Use an international number beginning with + and the country code.";
  if (candidate.country.trim().length < 2) errors.country = "Enter the current country of residence.";
  if (!candidate.consent) errors.consent = "Confirm the privacy and contact statement before continuing.";
  return errors;
}

function FieldError({ children }) {
  if (!children) return null;
  return <p className="mt-1.5 text-xs font-medium text-rose-700">{children}</p>;
}

export function Programmes() {
  return (
    <div className="max-w-7xl mx-auto px-5 py-14" data-testid="programmes-page">
      <p className="overline">EuropEdu programmes</p>
      <div className="mt-3 max-w-3xl">
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-950">
          Structured services, one reusable platform
        </h1>
        <p className="mt-5 text-base md:text-lg text-slate-600">
          Programme journeys combine information, initial assessment, human review and—once secure staging is enabled—case progress and document collection.
        </p>
      </div>

      <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {PROGRAMMES.map((programme) => (
          <Link
            key={programme.slug}
            to={programme.href || `/programmes/${programme.slug}`}
            className="card p-7 bg-white hover:-translate-y-0.5 transition-transform"
            data-testid={`programme-${programme.slug}`}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="overline">{programme.eyebrow}</p>
                <h2 className="mt-2 text-xl font-semibold text-slate-950">{programme.title}</h2>
              </div>
              <span className="pill bg-blue-50 text-blue-800">{programme.status}</span>
            </div>
            <p className="mt-4 text-sm text-slate-600">{programme.summary}</p>
            <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#1e3a8a]">
              Open programme <ArrowRight className="w-4 h-4" />
            </span>
          </Link>
        ))}
      </div>

      <div className="mt-12 rounded-2xl border border-slate-200 bg-slate-50 p-6 flex gap-4">
        <LockKeyhole className="w-5 h-5 text-slate-500 shrink-0 mt-0.5" />
        <div>
          <h2 className="font-semibold text-slate-900">Secure service boundaries</h2>
          <p className="mt-1 text-sm text-slate-600">
            Only the Innovator Founder assessment stores information, and only after you explicitly submit it for senior review. Study searches are public until you sign in to shortlist or apply; document collection remains inside the secure case workflow.
          </p>
        </div>
      </div>
    </div>
  );
}

export function InnovatorFounderProgramme() {
  const { user } = useAuth();
  const [candidate, setCandidate] = useState(emptyCandidate);
  const [candidateErrors, setCandidateErrors] = useState({});
  const [intakeComplete, setIntakeComplete] = useState(false);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [submission, setSubmission] = useState({ state: "idle", message: "" });

  useEffect(() => {
    if (!user) return;
    setCandidate((current) => ({
      ...current,
      fullName: current.fullName || user.full_name || "",
      email: user.email || "",
    }));
  }, [user]);

  const groups = useMemo(
    () => [...new Set(INNOVATOR_FOUNDER.questions.map((q) => q.group))],
    []
  );
  const answeredCount = Object.keys(answers).filter((key) => answers[key]).length;
  const result = assessInnovatorFounder(answers);
  const progress = Math.round((answeredCount / INNOVATOR_FOUNDER.questions.length) * 100);

  const updateCandidate = (field, value) => {
    setCandidate((current) => ({ ...current, [field]: value }));
    setCandidateErrors((current) => ({ ...current, [field]: undefined }));
  };

  const continueToAssessment = (event) => {
    event.preventDefault();
    if (!user) {
      setSubmission({ state: "error", message: "Sign in or create an account before starting the assessment." });
      return;
    }
    if (!user.email_verified) {
      setSubmission({ state: "error", message: "Verify your email from your dashboard before starting the assessment." });
      return;
    }
    const errors = validateCandidate(candidate);
    setCandidateErrors(errors);
    if (Object.keys(errors).length > 0) return;
    setIntakeComplete(true);
    setTimeout(() => document.getElementById("founder-assessment")?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
  };

  const editCandidate = () => {
    setIntakeComplete(false);
    setAnswers({});
    setShowResult(false);
    setTimeout(() => document.getElementById("pre-assessment-details")?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
  };

  const setAnswer = (id, value) => {
    setAnswers((current) => ({ ...current, [id]: value }));
    setShowResult(false);
  };

  const resetAssessment = () => {
    setAnswers({});
    setShowResult(false);
    document.getElementById("founder-assessment")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submitForReview = async () => {
    if (!user?.email_verified) {
      setSubmission({ state: "error", message: "A verified signed-in account is required before submission." });
      return;
    }
    if (answeredCount !== INNOVATOR_FOUNDER.questions.length) {
      setSubmission({ state: "error", message: "Complete every assessment question before submitting." });
      return;
    }
    setSubmission({ state: "saving", message: "Submitting your assessment securely…" });
    try {
      await api.post("/programmes/innovator-founder/assessments", {
        full_name: candidate.fullName, email: candidate.email, phone: candidate.phone,
        country: candidate.country, city: candidate.city,
        service_contact_consent: candidate.consent,
        whatsapp_marketing_consent: candidate.whatsappMarketing,
        answers, result,
      });
      setSubmission({ state: "success", message: "Assessment submitted for senior review. EuropEdu will contact you using the consent recorded above." });
    } catch (error) {
      setSubmission({ state: "error", message: error?.response?.data?.detail || "We could not submit the assessment. Please try again." });
    }
  };

  return (
    <div data-testid="innovator-founder-programme">
      <section className="bg-[#0f172a] text-white">
        <div className="max-w-7xl mx-auto px-5 py-16 md:py-20 grid gap-10 lg:grid-cols-[1.3fr_0.7fr] items-start">
          <div>
            <Link to="/programmes" className="inline-flex items-center gap-2 text-sm text-sky-300 hover:text-white">
              <ArrowLeft className="w-4 h-4" /> All programmes
            </Link>
            <p className="overline text-sky-300 mt-8">{INNOVATOR_FOUNDER.eyebrow}</p>
            <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold tracking-tight">{INNOVATOR_FOUNDER.title}</h1>
            <p className="mt-5 max-w-3xl text-slate-200 text-base md:text-lg">{INNOVATOR_FOUNDER.summary}</p>
            <div className="mt-7 flex flex-wrap gap-3 text-xs">
              <span className="pill bg-white/10 text-white"><UserRound className="w-3.5 h-3.5" /> Contact details first</span>
              <span className="pill bg-white/10 text-white"><LockKeyhole className="w-3.5 h-3.5" /> Submission requires explicit action</span>
              <span className="pill bg-white/10 text-white"><Scale className="w-3.5 h-3.5" /> Human review required</span>
            </div>
          </div>
          <div className="rounded-2xl border border-white/15 bg-white/5 p-6">
            <p className="overline text-sky-300">Current scope</p>
            <p className="mt-3 text-sm text-slate-200">
              This prototype screens the new-business pathway. Existing/previously endorsed business cases should be escalated for individual senior review rather than relying on this automated screen.
            </p>
            <p className="mt-4 text-xs text-slate-400">Official sources last reviewed: {INNOVATOR_FOUNDER.reviewed}</p>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 py-14 grid gap-10 lg:grid-cols-[minmax(0,1fr)_360px] items-start">
        <div>
          {!intakeComplete ? (
            <div id="pre-assessment-details" className="scroll-mt-28" data-testid="if-preassessment-intake">
              <p className="overline">Step 1 of 2</p>
              <h2 className="mt-2 text-3xl font-semibold text-slate-950">Tell us who is taking the assessment</h2>
              <p className="mt-3 max-w-3xl text-sm text-slate-600 leading-6">
                Enter basic contact and location details before starting. Details are validated and stored securely only when you submit the completed assessment for senior review.
              </p>

              {!user && <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">A signed-in account is required. <Link className="font-semibold underline" to="/login">Sign in</Link> or <Link className="font-semibold underline" to="/register">create an account</Link>.</div>}
              {user && !user.email_verified && <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">Verify {user.email} from <Link className="font-semibold underline" to="/dashboard">your dashboard</Link> before continuing.</div>}

              <form onSubmit={continueToAssessment} className="mt-8 card bg-white p-6 md:p-8 space-y-6" noValidate>
                <div className="grid gap-6 md:grid-cols-2">
                  <label className="block md:col-span-2">
                    <span className="text-sm font-semibold text-slate-900">Full name <span className="text-rose-600">*</span></span>
                    <div className="relative mt-2">
                      <UserRound className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        value={candidate.fullName}
                        onChange={(e) => updateCandidate("fullName", e.target.value)}
                        autoComplete="name"
                        className={`field !pl-11 w-full ${candidateErrors.fullName ? "border-rose-400" : ""}`}
                        placeholder="Candidate full name"
                        data-testid="if-intake-name"
                        aria-invalid={Boolean(candidateErrors.fullName)}
                      />
                    </div>
                    <FieldError>{candidateErrors.fullName}</FieldError>
                  </label>

                  <label className="block">
                    <span className="text-sm font-semibold text-slate-900">Email address <span className="text-rose-600">*</span></span>
                    <div className="relative mt-2">
                      <Mail className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                      <input
                        type="email"
                        value={candidate.email}
                        onChange={(e) => updateCandidate("email", e.target.value)}
                        readOnly={Boolean(user)}
                        autoComplete="email"
                        className={`field !pl-11 w-full ${candidateErrors.email ? "border-rose-400" : ""}`}
                        placeholder="name@example.com"
                        data-testid="if-intake-email"
                        aria-invalid={Boolean(candidateErrors.email)}
                      />
                    </div>
                    <FieldError>{candidateErrors.email}</FieldError>
                  </label>

                  <label className="block">
                    <span className="text-sm font-semibold text-slate-900">Mobile / WhatsApp <span className="text-rose-600">*</span></span>
                    <div className="relative mt-2">
                      <Phone className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                      <input
                        type="tel"
                        value={candidate.phone}
                        onChange={(e) => updateCandidate("phone", e.target.value)}
                        autoComplete="tel"
                        className={`field !pl-11 w-full ${candidateErrors.phone ? "border-rose-400" : ""}`}
                        placeholder="+44 7534 560589"
                        data-testid="if-intake-phone"
                        aria-invalid={Boolean(candidateErrors.phone)}
                      />
                    </div>
                    <FieldError>{candidateErrors.phone}</FieldError>
                  </label>

                  <label className="block">
                    <span className="text-sm font-semibold text-slate-900">Current country of residence <span className="text-rose-600">*</span></span>
                    <div className="relative mt-2">
                      <MapPin className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        value={candidate.country}
                        onChange={(e) => updateCandidate("country", e.target.value)}
                        autoComplete="country-name"
                        className={`field !pl-11 w-full ${candidateErrors.country ? "border-rose-400" : ""}`}
                        placeholder="United Kingdom"
                        data-testid="if-intake-country"
                        aria-invalid={Boolean(candidateErrors.country)}
                      />
                    </div>
                    <FieldError>{candidateErrors.country}</FieldError>
                  </label>

                  <label className="block">
                    <span className="text-sm font-semibold text-slate-900">City / region <span className="text-slate-400 font-normal">(optional)</span></span>
                    <div className="relative mt-2">
                      <MapPin className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        value={candidate.city}
                        onChange={(e) => updateCandidate("city", e.target.value)}
                        autoComplete="address-level2"
                        className="field !pl-11 w-full"
                        placeholder="Crawley, West Sussex"
                        data-testid="if-intake-city"
                      />
                    </div>
                  </label>
                </div>

                <div className={`rounded-xl border p-4 ${candidateErrors.consent ? "border-rose-300 bg-rose-50" : "border-slate-200 bg-slate-50"}`}>
                  <label className="flex gap-3 items-start cursor-pointer">
                    <input
                      type="checkbox"
                      checked={candidate.consent}
                      onChange={(e) => updateCandidate("consent", e.target.checked)}
                      className="mt-1 h-4 w-4"
                      data-testid="if-intake-consent"
                    />
                    <span className="text-sm text-slate-700 leading-6">
                      I confirm these contact details are mine, I have read the <Link to="/legal/privacy-notice" className="font-semibold text-[#1e3a8a] hover:underline">Privacy Notice</Link>, and I understand EuropEdu may contact me about this assessment. This does not opt me into unrelated marketing.
                    </span>
                  </label>
                  <FieldError>{candidateErrors.consent}</FieldError>
                </div>

                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                  <label className="flex gap-3 items-start cursor-pointer">
                    <input
                      type="checkbox"
                      checked={candidate.whatsappMarketing}
                      onChange={(e) => updateCandidate("whatsappMarketing", e.target.checked)}
                      className="mt-1 h-4 w-4"
                      data-testid="if-intake-whatsapp-marketing"
                    />
                    <span className="text-sm text-emerald-950 leading-6">
                      <strong>Optional WhatsApp updates and offers:</strong> I would like EuropEdu to send me WhatsApp messages at the number above about programme updates, consultation availability and relevant EuropEdu immigration, education or recruitment service offers. I understand this is optional, it does not affect my assessment, and I can opt out at any time.
                    </span>
                  </label>
                </div>

                <div className="rounded-xl bg-blue-50 border border-blue-100 p-4 flex gap-3">
                  <LockKeyhole className="w-5 h-5 text-[#1e3a8a] shrink-0 mt-0.5" />
                  <p className="text-sm text-blue-950 leading-6">
                    <strong>Privacy:</strong> your signed-in email must be verified before submission. Your assessment is stored only when you explicitly submit it for senior review, together with the consent wording, terms version, timestamp and account identity.
                  </p>
                </div>

                <button type="submit" disabled={!user?.email_verified} className="btn btn-primary" data-testid="if-intake-continue">
                  Continue to assessment <ArrowRight className="w-4 h-4" />
                </button>
              </form>
            </div>
          ) : (
            <div id="founder-assessment" className="scroll-mt-28" data-testid="if-assessment-active">
              <div className="rounded-2xl border border-blue-100 bg-blue-50 p-5 mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-blue-700">Assessment participant</p>
                  <p className="mt-1 font-semibold text-slate-950">{candidate.fullName}</p>
                  <p className="mt-1 text-sm text-slate-600">{candidate.email} · {candidate.phone} · {candidate.city ? `${candidate.city}, ` : ""}{candidate.country}</p>
                  <p className="mt-2 text-xs text-slate-500">WhatsApp marketing: {candidate.whatsappMarketing ? "opted in" : "not opted in"} · Submission requires your explicit action below.</p>
                </div>
                <button type="button" onClick={editCandidate} className="btn btn-ghost text-sm shrink-0">
                  Edit details
                </button>
              </div>

              <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                  <p className="overline">Step 2 of 2 · Initial assessment</p>
                  <h2 className="mt-2 text-3xl font-semibold text-slate-950">Founder and business readiness</h2>
                  <p className="mt-3 max-w-3xl text-sm text-slate-600">
                    Answer based on your current position. “Not sure” is better than guessing; uncertainty becomes a review flag rather than an automatic rejection.
                  </p>
                </div>
                <span className="text-sm font-semibold text-slate-700">{answeredCount}/{INNOVATOR_FOUNDER.questions.length} answered</span>
              </div>

              <div className="mt-6 h-2 rounded-full bg-slate-100 overflow-hidden" aria-label={`Assessment ${progress}% complete`}>
                <div className="h-full bg-[#1e3a8a] transition-all" style={{ width: `${progress}%` }} />
              </div>

              <div className="mt-10 space-y-10">
                {groups.map((group) => (
                  <section key={group}>
                    <h3 className="text-xl font-semibold text-slate-950">{group}</h3>
                    <div className="mt-4 space-y-4">
                      {INNOVATOR_FOUNDER.questions.filter((q) => q.group === group).map((q) => (
                        <fieldset key={q.id} className="card p-6 bg-white" data-testid={`if-question-${q.id}`}>
                          <legend className="sr-only">{q.prompt}</legend>
                          <div className="flex gap-4">
                            <span className="w-7 h-7 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center text-xs font-bold shrink-0">
                              {INNOVATOR_FOUNDER.questions.findIndex((item) => item.id === q.id) + 1}
                            </span>
                            <div className="min-w-0 flex-1">
                              <p className="font-semibold text-slate-950">{q.prompt}</p>
                              <p className="mt-2 text-sm text-slate-500">{q.help}</p>
                              <div className="mt-4 flex flex-wrap gap-2">
                                {answerOptions(q.allowNotApplicable).map((option) => {
                                  const active = answers[q.id] === option.value;
                                  return (
                                    <button
                                      type="button"
                                      key={option.value}
                                      onClick={() => setAnswer(q.id, option.value)}
                                      className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                                        active
                                          ? "border-[#1e3a8a] bg-blue-50 text-[#1e3a8a]"
                                          : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                                      }`}
                                      aria-pressed={active}
                                    >
                                      {option.label}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        </fieldset>
                      ))}
                    </div>
                  </section>
                ))}
              </div>

              <div className="mt-10 flex flex-wrap gap-3">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowResult(true)}
                  data-testid="if-generate-result"
                >
                  Generate initial review summary <ArrowRight className="w-4 h-4" />
                </button>
                {showResult && (
                  <button type="button" className="btn btn-secondary" onClick={submitForReview} disabled={submission.state === "saving" || submission.state === "success"} data-testid="if-submit-review">
                    {submission.state === "saving" ? "Submitting…" : "Submit for senior review"} <ArrowRight className="w-4 h-4" />
                  </button>
                )}
                <button type="button" className="btn btn-ghost" onClick={resetAssessment}>
                  <RotateCcw className="w-4 h-4" /> Reset assessment
                </button>
              </div>

              {showResult && (
                <div className={`mt-8 rounded-2xl border p-6 ${toneClasses[result.tone] || toneClasses.amber}`} data-testid="if-result">
                  <div className="flex gap-3">
                    {result.key === "potential" ? <CheckCircle2 className="w-6 h-6 shrink-0" /> : <CircleAlert className="w-6 h-6 shrink-0" />}
                    <div>
                      <h3 className="text-xl font-semibold">{result.title}</h3>
                      <p className="mt-2 text-sm leading-6">{result.text}</p>
                      {result.flags.length > 0 && (
                        <div className="mt-5">
                          <p className="text-sm font-semibold">Items to review</p>
                          <ul className="mt-2 list-disc pl-5 space-y-1 text-sm">
                            {result.flags.map((flag) => <li key={flag}>{flag}</li>)}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                  {submission.message && <p className={`mt-5 text-sm font-medium ${submission.state === "error" ? "text-rose-700" : "text-slate-700"}`} role="status">{submission.message}</p>}
                </div>
              )}
            </div>
          )}
        </div>

        <aside className="space-y-6 lg:sticky lg:top-24">
          <div className="card p-6 bg-white">
            <div className="flex items-center gap-2 text-slate-900">
              <Lightbulb className="w-5 h-5 text-amber-500" />
              <h2 className="font-semibold">What this assessment does</h2>
            </div>
            <p className="mt-3 text-sm text-slate-600">
              It organises information and identifies readiness gaps against current published route factors. It does not issue an endorsement, approve a visa or replace regulated immigration advice.
            </p>
          </div>

          <div className="card p-6 bg-white">
            <div className="flex items-center gap-2 text-slate-900">
              <FileCheck2 className="w-5 h-5 text-[#1e3a8a]" />
              <h2 className="font-semibold">Future document workspace</h2>
            </div>
            <p className="mt-3 text-sm text-slate-600">
              After the secure staging backend is ready, approved cases can receive a tailored checklist and private upload workspace. Uploads are intentionally disabled in this preview.
            </p>
            <div className="mt-4 space-y-3">
              {INNOVATOR_FOUNDER.documents.slice(0, 6).map((doc) => (
                <div key={doc.title} className="rounded-xl bg-slate-50 p-3">
                  <p className="text-sm font-medium text-slate-800">{doc.title}</p>
                  <p className="mt-1 text-xs text-slate-500">{doc.type}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="card p-6 bg-white">
            <h2 className="font-semibold text-slate-900">Official sources</h2>
            <ul className="mt-4 space-y-3 text-sm">
              {INNOVATOR_FOUNDER.sources.map((source) => (
                <li key={source.href}>
                  <a href={source.href} target="_blank" rel="noreferrer" className="text-[#1e3a8a] font-medium hover:underline">
                    {source.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </section>
    </div>
  );
}
